/**
 * client.func.ts - UI 관련 함수
 */
import {ElementRef, Renderer2} from "@angular/core";
import {JASO, REGION, REGION2, REGION2_DEFAULT} from "./gukeo.const";
import {SEARCH_KEYWORD_MAX_LENGTH, SearchParam} from "./client.const";
import {IsContainHttp, IsEmptyString} from "./common.func";
import {GUKEO_API_URL1} from "./protocol";
import {SaseungHanja} from "../models/saseung.model";


export function NormalizeSearchKeyword(keyword: unknown): string {
  return String(keyword ?? '').trim().slice(0, SEARCH_KEYWORD_MAX_LENGTH);
}


/**
 * html element에 large-mode 클래스를 추가/제거합니다.
 * @param renderer
 * @param el
 * @param large true: large-mode 클래스 추가, false: large-mode 클래스 제거
 */
export function SetHtmlLargeMode(renderer: Renderer2, el: ElementRef, large: boolean = true): void {
  // html 요소를 가져옵니다.
  const htmlElement = el.nativeElement.ownerDocument.documentElement;

  if (large) {
    // 클래스를 추가합니다.
    renderer.addClass(htmlElement, 'large-mode');
  }
  else {
    // 클래스를 제거합니다.
    renderer.removeClass(htmlElement, 'large-mode');
  }
}


/**
 * Angular 라우터 파라미터 (QueryParam) 에서 서버 API 파라미터 (SearchParam) 를 얻는다.
 *
 * 자소 색인, 입력창 검색 키워드, 지역 등의 중복 혹은 배치 규칙을 처리한다. 기획적인 부분이 밀접하다.
 */
export function GetSearchFromQuery(param: SearchParam | null, jaso: JASO | null = null ): [SearchParam, JASO | null, string | null, REGION2] {
  let searchParam: SearchParam = IsEmptyString(jaso) ? {} : { index: jaso as string };
  let jasoIdx = IsEmptyString(jaso) ? null : jaso;
  let keyword: string | null = null;
  let region: REGION2 = REGION2_DEFAULT;

  if (param) {
    // if (param.id) {
    //   // id가 있으면 id로 검색한다.
    //   // 책상형 홈피에만 존재하는 기능이다.
    //   searchParam = param;
    //   jasoIdx = null;
    // }
    // else
    const normalizedKeyword = NormalizeSearchKeyword(param.keyword);
    if (normalizedKeyword) {
      searchParam = {
        ...param,
        keyword: normalizedKeyword,
      };
      jasoIdx = null;
      keyword = normalizedKeyword;
    }
    else if (param?.index) {
      jasoIdx = param.index as JASO;
      searchParam.index = param.index;
    }

    if (param?.region2) {
      searchParam.region2 = param.region2;
      region = param.region2 as REGION2;
    }

    if (param?.start) {
      searchParam.start = param.start;
    }
    else if (param?.page) {
      searchParam.page = param.page;
    }
  }
  console.warn(`GetSearchFromQuery() keyword=${keyword}`, param, searchParam, jasoIdx, region);
  return [searchParam, jasoIdx, keyword, region];
}

/**
 * @deprecated 신규 호출자는 추가하지 말 것. 호출자가 모두 사라지면 함수도 함께 제거한다.
 * 이 함수는 `?id=X`만 있고 `start`가 없을 때 anchor를 합성하던 과거 보조 함수다.
 * 정책상 컴포넌트의 init()에서 `id`/`start`를 분리해서 다루어야 하므로,
 * 이 함수를 호출해 기존 `start`를 덮어쓰면 목록 anchor가 깨지고 책갈피/검색 맥락이 사라진다.
 * 대체: 컴포넌트의 init()에서 `GetSearchFromQuery()`로 `start`를 살리고,
 * `start`가 없을 때만 `param.start = String(queryParams.id)`로 합성한다.
 * Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
 */
export function GetDirectIdSearchParam(id: string | number): SearchParam {
  return { start: String(id) };
}

/**
 * @deprecated 목록 항목 선택 라우팅에는 사용하지 않는다.
 * Spec: docs/page_common_logic.md > 선택 URL 히스토리와 start/id 보존
 */
export function CleanDirectIdQuery(param: Record<string, any>): SearchParam {
  const queryParams: SearchParam = { ...param };
  delete queryParams.start;
  delete queryParams.page;
  delete queryParams.keyword;
  delete queryParams.index;
  delete queryParams.level;
  delete queryParams.hanja;
  delete queryParams.hun;
  delete queryParams.hangul;
  delete queryParams.word_id;
  delete queryParams.region;
  delete queryParams.region2;
  delete queryParams.city;
  delete queryParams.cat;
  delete queryParams.pp;
  delete queryParams.postposition;
  delete queryParams.example_cat;
  return queryParams;
}


/**
 * HTMLElement의 부모 요소를 재귀적으로 확인합니다.
 * @param element
 * @param parent
 */
export function isParentHTMLElement(element: HTMLElement, parent: HTMLElement): boolean {
  if (!element) {
    return false; // element가 유효하지 않은 경우 false를 반환합니다.
  }

  if (element.parentNode === parent) {
    return true; // 부모 요소를 찾았으므로 true를 반환합니다.
  }

  return isParentHTMLElement(element.parentNode as HTMLElement, parent); // 부모 요소를 재귀적으로 확인합니다.
}


/**
 * HTMLElement에서 padding을 제외한 안쪽 영역의 크기를 가져오는 함수
 * @param element
 */
export function getContentSize(element: HTMLElement): { width: number, height: number } {
  const style = window.getComputedStyle(element);

  const paddingTop = parseInt(style.paddingTop, 10);
  const paddingRight = parseInt(style.paddingRight, 10);
  const paddingBottom = parseInt(style.paddingBottom, 10);
  const paddingLeft = parseInt(style.paddingLeft, 10);
  // console.warn('getContentSize()', element.offsetWidth, element.offsetHeight, paddingLeft, paddingRight, paddingTop, paddingBottom);

  const width = element.offsetWidth - paddingLeft - paddingRight;
  const height = element.offsetHeight - paddingTop - paddingBottom;
  // console.warn('getContentSize()', width, height);

  return { width, height };
}


export function makeImageUrl(image: string | null): string {
  if (image === null) {
    return 'assets/img/img_empty_video.png';
  }
  return IsContainHttp(image) ? image : GUKEO_API_URL1 + image;
}


export function makeYoutubeUrl(youtubeId: string): string {
  return `https://i.ytimg.com/vi/${youtubeId}/mqdefault.jpg`;
}


/**
 * HTMLElement의 스크롤 부모를 가져오는 함수
 * 바로 위의 요소가 스크롤 부모가 아닐 수 있으므로 재귀적으로 확인합니다.
 * @param element
 */
export function getScrollParent(element: Element) {
  let style = getComputedStyle(element);
  const excludeStaticParent = style.position === "absolute";
  const overflowRegex = /(auto|scroll)/;

  if (style.position === "fixed") return document.body;

  for (let parent: Element | null = element; (parent = parent.parentElement);) {
    style = getComputedStyle(parent);
    if (excludeStaticParent && style.position === "static") {
      continue;
    }
    if (overflowRegex.test(style.overflow + style.overflowY + style.overflowX))
      return parent;
  }

  return document.body;
}


const LIST_ITEM_ID = 'list-id-';

/**
 * 왼쪽 목록에서 사용되는 아이템의 ID를 만든다. 스크롤을 위해서 해당 아이템을 찾는데 쓰인다.
 * @param id
 */
export function listItemId(id: string): string {
  // return LIST_ITEM_ID + id;
  return id;
}


// Query Parameters를 입력 받아 URL을 만드는 함수
export function generateUrlWithQueryParams(basePath: string, params: { [key: string]: any }): string {
  const queryParams = new URLSearchParams();

  // 객체의 각 key-value 쌍을 순회
  for (const key of Object.keys(params)) {
    queryParams.set(key, params[key]);
  }

  // 전체 URL 만들기
  return `${basePath}?${queryParams.toString()}`;
}

export function getQueryParamsFromUrl(url: string): Record<string, string> {
  // URL에서 쿼리 문자열을 추출합니다.
  const queryString = url.split('?')[1];
  if (!queryString) {
    return {};
  }

  // 쿼리 문자열을 '&' 기호로 분리하여 각 파라미터를 배열로 변환합니다.
  const paramsArray = queryString.split('&');

  // 결과 객체를 생성합니다.
  const paramsObj: Record<string, string> = {};

  // 배열의 각 요소(파라미터)를 순회하면서 객체에 추가합니다.
  paramsArray.forEach(param => {
    const [key, value] = param.split('=');
    if (key) paramsObj[key] = decodeURIComponent(value || '');
  });

  console.warn('getQueryParamsFromUrl()', url, paramsObj);
  return paramsObj;
}


export function GetHanjaHtml(hanja: string, highlight: string | null = null): string {
  let hanjaHtml = "";

  // 문자열에 UTF-8 MB4가 존재하므로 주의해서 처리해야 한다.
  Array.from(hanja).forEach((char: string, idx: number) => {
    if (highlight && char === highlight) {
      char = '<B>' + char + '</B>';
    }
    if (idx === 0) {
      hanjaHtml = char;
    } else if (idx === 1) {
      hanjaHtml += '<span class="hanja-size">' + char;
    } else if (idx % 2 === 0) {
      hanjaHtml += '<br/>' + char;
    } else {
      hanjaHtml += char;
    }
  });
  if (hanja.length > 1) {
    hanjaHtml += '</span>';
  }
  return hanjaHtml;
}


/**
 * 뜻에서 선택된 훈을 강조하는 함수.
 * 훈 사이에 공백이 일정하지 않을 수 있어서 번거롭게 처리함.
 *
 * @param meaning
 * @param hun
 * @constructor
 */
export function SetHunHightlight(meaning: string, hun: string): string {
  if (IsEmptyString(hun)) {
    return meaning;
  }

  const huns: string[] = meaning.split(',')
  const huns2 = huns.map(hun1 => {
    if (hun == hun1.trim()) {
      return hun1.replace(hun, `<span class="highlight">${hun}</span>`);
    }
    return hun1;
  });

  return huns2.join(',');
}
